import React, { useState, useRef, useEffect } from 'react';
import { Button, Select, Icon } from '@repo/ui';
import type { SelectOption } from '@repo/ui';

interface BulkAssignBarProps {
  selectedCount: number;
  assigneeOptions: SelectOption[];
  onAssigneeChange: (value: string) => void;
  isBulkAssigning: boolean;
  currentAssignedTo?: string | null;
}

/**
 * @description Reusable Bulk Assign bar that shows when items are selected.
 * - If all selected items have the same clerk: shows Select with that clerk pre-selected
 * - Otherwise: shows a button to reveal clerk selector
 */
const BulkAssignBar: React.FC<BulkAssignBarProps> = ({
  selectedCount,
  assigneeOptions,
  onAssigneeChange,
  isBulkAssigning,
  currentAssignedTo,
}) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close select when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  // Reset unassigning state when bulk assigning completes
  useEffect(() => {
    if (!isBulkAssigning) {
      setIsUnassigning(false);
    }
  }, [isBulkAssigning]);

  if (selectedCount === 0) return null;

  const handleClerkSelect = (value: string) => {
    if (value) {
      onAssigneeChange(value);
      setIsSelectOpen(false);
    }
  };

  const handleUnassign = () => {
    setIsUnassigning(true);
    onAssigneeChange('');
  };

  const isLoading = isBulkAssigning;

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-2 mt-2 p-2 bg-xtnd-light/50 dark:bg-xtnd-dark/50 rounded-md border border-xtnd-light dark:border-xtnd-dark">
      <div className="flex items-center gap-2 sm:mr-auto mb-2 sm:mb-0">
        <span className="text-sm text-xtnd-dark dark:text-xtnd-light font-bold whitespace-nowrap">
          {selectedCount} item{selectedCount === 1 ? '' : 's'} selected
        </span>
      </div>

      <div
        ref={containerRef}
        className="relative flex flex-wrap items-center gap-2 w-full sm:w-auto"
      >
        <Button
          variant="destructive"
          size="md"
          onClick={handleUnassign}
          loading={isUnassigning}
          disabled={isLoading}
          className="flex items-center gap-2 text-sm"
        >
          {!isUnassigning && <Icon name="FaUserMinus" size="sm" />}
          {isUnassigning ? 'Unassigning...' : 'Unassign Clerk'}
        </Button>
        {isSelectOpen ? (
          <Select
            options={assigneeOptions}
            value={currentAssignedTo || ''}
            onChange={handleClerkSelect as any}
            placeholder="Search clerk..."
            className="text-sm w-[15rem]"
            menuPlacement="auto"
            menuPosition="fixed"
            searchable={true}
            isLoading={isLoading && !isUnassigning}
            disabled={isLoading}
          />
        ) : (
          <Button
            variant="outline"
            size="md"
            onClick={() => setIsSelectOpen(true)}
            loading={isLoading && !isUnassigning}
            disabled={isLoading}
            className="flex items-center gap-2 text-sm"
          >
            {!(isLoading && !isUnassigning) && <Icon name="FaUserPlus" size="sm" />}
            {isLoading && !isUnassigning ? 'Assigning...' : 'Assign to Clerk'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BulkAssignBar;
